/***
 *
 *   SIGN UP STEP 1
 *   Create the email and password.
 *
 **********/

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  AuthContext,
  ViewContext,
  Checkbox,
  Form,
  Link,
  Separator,
  SocialSignin,
  Turnstile,
  useNavigate,
  useToast,
} from "components/lib";

export function Signup({ t }) {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const { dismiss } = useToast();
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const handleTurnstileVerify = useCallback((token) => setTurnstileToken(token), []);

  useEffect(() => {
    // Dismiss all existing toasts first
    dismiss();

    // Show free tier message only if not shown in last 2 hours
    const lastShown = localStorage.getItem("freeTierToastShown");
    const now = Date.now();
    const twoHours = 2 * 60 * 60 * 1000;

    if (!lastShown || now - parseInt(lastShown) > twoHours) {
      viewContext.notification({
        title: t("auth.free_tier.title") || "Free to get started!",
        description: t("auth.free_tier.description") || "Sign up for free - no credit card needed to create an account and use the free tier.",
        variant: "info",
        duration: Infinity,
      });
      localStorage.setItem("freeTierToastShown", now.toString());
    }
  }, []);

  return (
    <div className="">
      <div className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-light text-white fade-in tracking-wide">
          {t("auth.signup.account.title")}
        </h1>
        <p className="text-white/60 text-sm font-light slide-up" style={{animationDelay: '0.1s'}}>
          Create your account to access analytics dashboard
        </p>
      </div>

      <div className="mt-8"></div>

      {/* <SocialSignin network={["facebook", "twitter"]} signup /> */}
      {/* <Separator label={t("global.social.or")} /> */}

      <div className="scale-in" style={{animationDelay: '0.3s'}}>
        <Form
          inputs={{
          name: {
            label: t("auth.signup.account.form.name.label"),
            type: "text",
            required: true,
            errorMessage: t("auth.signup.account.form.name.error"),
          },
          email: {
            label: t("auth.signup.account.form.email.label"),
            type: "email",
            required: true,
          },
          username: {
            label: t("auth.signup.account.form.username.label"),
            type: "text",
            required: true,
            errorMessage: t("auth.signup.account.form.username.error"),
          },
          password: {
            label: t("auth.signup.account.form.password.label"),
            type: "password",
            required: true,
            validation: { complex: true },
          },
          terms: {
            label: (
              <>
                {t("auth.signup.account.form.terms.text")}{" "}
                <Link
                  url="/terms"
                  text={t("auth.signup.account.form.terms.terms_of_use")}
                  className="text-white/60 hover:text-white/90 text-xs font-light tracking-wide transition-colors duration-200 hover:underline"
                />{" "}
                &{" "}
                <Link
                  url="/privacy"
                  text={t("auth.signup.account.form.terms.privacy_policy")}
                  className="text-white/60 hover:text-white/90 text-xs font-light tracking-wide transition-colors duration-200 hover:underline"
                />
                .
              </>
            ),
            type: "checkbox",
            required: true,
            errorMessage:
              "You must agree to the terms and conditions to proceed.",
          },
          confirm_password: {
            type: "hidden",
            value: null,
          },
        }}
        url="/api/account"
        method="POST"
        buttonText={t("auth.signup.account.form.button")}
        extraData={{ turnstile_token: turnstileToken }}
        onSubmitError={() => turnstileRef.current?.reset()}
        callback={(res) => navigate(authContext.signin(res))}
        >
          <Turnstile
            ref={turnstileRef}
            className="mb-4"
            onVerify={handleTurnstileVerify}
          />
        </Form>
      </div>

      <footer className="justify-center text-xs space-x-2 slide-up font-light" style={{animationDelay: '0.5s'}}>
        <span className="text-white/50">{t("auth.signup.user.footer.text")}</span>
        <Link
          url="/signin"
          text={t("auth.signup.user.footer.link_text")}
          className="text-white/80 hover:text-white tracking-wide transition-colors duration-200 hover:underline"
        />
      </footer>
    </div>
  );
}
